import { useCallback, useMemo } from 'react';
import Select, { type MultiValue, type OptionProps, type StylesConfig } from 'react-select';
import { type LabelOptions, useLabelOptions } from '../../hooks/label-options';
import { baseSelectStyles, brandedSelectTheme } from '../form-styles';

const base = baseSelectStyles<LabelOptions, true>();

const styles: StylesConfig<LabelOptions, true> = {
  ...base,
  control: (styles, state) => ({
    ...(base.control?.(styles, state) ?? styles),
    minWidth: '160px',
  }),
  menuList: (styles) => ({
    ...styles,
    maxHeight: '180px',
    overflow: 'auto',
  }),
  multiValue: (styles, { data }) => ({
    ...styles,
    backgroundColor: data.color,
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: 'white',
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: 'white',
    ':hover': {
      color: '#cccccc',
    },
  }),
};

const Option = (props: OptionProps<LabelOptions>) => {
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: need to not use option to allow for children elements
    <div
      className="flex flex-row gap-2 items-center px-4 py-2 whitespace-nowrap cursor-pointer hover:bg-gray-100"
      onClick={() => props.selectOption(props.data)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          props.selectOption(props.data);
        }
      }}
    >
      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: props.data.color }} />
      <div className="text-sm">{props.data.label}</div>
    </div>
  );
};

type StoreLabelsInputProps = {
  value: number[];
  onChange: (value: number[]) => void;
};

export function StoreLabelsInput({ value, onChange }: StoreLabelsInputProps) {
  const labelOptions = useLabelOptions();
  const selectValue = useMemo(() => {
    return labelOptions.filter((option) => value.includes(Number(option.value)));
  }, [labelOptions, value]);
  const handleChange = useCallback(
    (options: MultiValue<LabelOptions>) => {
      onChange(options.map((option) => Number(option.value)));
    },
    [onChange]
  );

  return (
    <Select
      isMulti
      options={labelOptions}
      value={selectValue}
      onChange={handleChange}
      theme={brandedSelectTheme}
      styles={styles}
      isSearchable={false}
      components={{ Option }}
      menuPortalTarget={document.body}
    />
  );
}
